import React, { useState, useEffect } from 'react';
import { CreditCard, Plus, Edit2, Trash2, Users, DollarSign, Check, X } from 'lucide-react';
import Sidebar from '../components/Sidebar';
import api from '../services/api';
import { toast } from 'sonner';

const MainAdminMembershipPlans = () => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    yearly_price: '',
    lifetime_price: '',
    billing_cycle: 'monthly',
    user_limit: 5,
    client_limit: 100,
    show_client_limit: true,
    loi_verifications_per_month: '',
    features: '',
    trial_days: 14,
    is_active: true,
    is_popular: false,
    badge_text: '',
    cta_text: ''
  });

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      setLoading(true);
      const response = await api.get('/main-admin/membership-plans');
      setPlans(response.data);
    } catch (error) {
      toast.error('Failed to fetch membership plans');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        price: parseFloat(formData.price) || 0,
        yearly_price: formData.yearly_price ? parseFloat(formData.yearly_price) : null,
        lifetime_price: formData.lifetime_price ? parseFloat(formData.lifetime_price) : null,
        loi_verifications_per_month: formData.loi_verifications_per_month ? parseInt(formData.loi_verifications_per_month) : null,
        features: formData.features.split('\n').filter(f => f.trim())
      };

      if (editingPlan) {
        await api.put(`/main-admin/membership-plans/${editingPlan.id}`, payload);
        toast.success('Plan updated successfully');
      } else {
        await api.post('/main-admin/membership-plans', payload);
        toast.success('Plan created successfully');
      }

      setShowModal(false);
      resetForm();
      fetchPlans();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save plan');
    }
  };

  const handleDelete = async (planId) => {
    if (!window.confirm('Are you sure you want to deactivate this plan?')) return;
    try {
      await api.delete(`/main-admin/membership-plans/${planId}?force=true`);
      toast.success('Plan deactivated');
      fetchPlans();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete plan');
    }
  };

  const openEditModal = (plan) => {
    setEditingPlan(plan);
    setFormData({
      name: plan.name,
      description: plan.description || '',
      price: plan.price,
      yearly_price: plan.yearly_price != null ? plan.yearly_price : '',
      lifetime_price: plan.lifetime_price != null ? plan.lifetime_price : '',
      billing_cycle: plan.billing_cycle,
      user_limit: plan.user_limit,
      client_limit: plan.client_limit,
      show_client_limit: plan.show_client_limit !== false,
      loi_verifications_per_month: plan.loi_verifications_per_month != null ? plan.loi_verifications_per_month : '',
      features: (plan.features || []).join('\n'),
      trial_days: plan.trial_days,
      is_active: plan.is_active,
      is_popular: plan.is_popular || false,
      badge_text: plan.badge_text || '',
      cta_text: plan.cta_text || ''
    });
    setShowModal(true);
  };

  const resetForm = () => {
    setEditingPlan(null);
    setFormData({
      name: '',
      description: '',
      price: '',
      yearly_price: '',
      lifetime_price: '',
      billing_cycle: 'monthly',
      user_limit: 5,
      client_limit: 100,
      show_client_limit: true,
      loi_verifications_per_month: '',
      features: '',
      trial_days: 14,
      is_active: true,
      is_popular: false,
      badge_text: '',
      cta_text: ''
    });
  };

  return (
    <Sidebar>
      <div className="p-6 lg:p-8">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Membership Plans</h1>
              <p className="text-gray-600 mt-1">Manage subscription plans and pricing</p>
            </div>
            <button
              onClick={() => { resetForm(); setShowModal(true); }}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
            >
              <Plus className="w-4 h-4" />
              Add Plan
            </button>
          </div>

          {/* Plans Grid */}
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full mx-auto"></div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {plans.map((plan) => (
                <div
                  key={plan.id}
                  className={`bg-white rounded-xl border-2 overflow-visible relative ${
                    plan.is_popular ? 'border-amber-400' :
                    plan.is_active ? 'border-gray-200' : 'border-gray-200 opacity-60'
                  }`}
                  style={plan.is_popular ? { boxShadow: '0 4px 24px rgba(229,162,37,0.15)' } : {}}
                >
                  {/* Most Popular Banner */}
                  {plan.is_popular && (
                    <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 px-5 py-1 bg-amber-500 text-white text-xs font-bold uppercase tracking-wide rounded-full whitespace-nowrap">
                      {plan.badge_text || 'Most Popular'}
                    </div>
                  )}

                  {/* Plan Header */}
                  <div className="p-6 border-b border-gray-100">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-xl font-bold text-gray-900">{plan.name}</h3>
                      {!plan.is_active && (
                        <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">Inactive</span>
                      )}
                    </div>
                    <div className="flex items-baseline gap-1">
                      <span className="text-3xl font-bold text-gray-900">${plan.price}</span>
                      <span className="text-gray-500">/mo</span>
                    </div>
                    {(plan.yearly_price || plan.lifetime_price) && (
                      <div className="flex gap-3 mt-1 text-sm text-gray-500">
                        {plan.yearly_price && <span>${plan.yearly_price}/yr</span>}
                        {plan.lifetime_price && <span>${plan.lifetime_price} lifetime</span>}
                      </div>
                    )}
                    {plan.description && (
                      <p className="mt-2 text-sm text-gray-600">{plan.description}</p>
                    )}
                  </div>

                  {/* Plan Details */}
                  <div className="p-6 space-y-4">
                    {/* Limits */}
                    <div className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2 text-gray-600">
                        <Users className="w-4 h-4" />
                        <span>User Limit</span>
                      </div>
                      <span className="font-semibold text-gray-900">{plan.user_limit}</span>
                    </div>
                    {plan.show_client_limit !== false && (
                      <div className="flex items-center justify-between text-sm">
                        <div className="flex items-center gap-2 text-gray-600">
                          <Users className="w-4 h-4" />
                          <span>Client Limit</span>
                        </div>
                        <span className="font-semibold text-gray-900">{plan.client_limit}</span>
                      </div>
                    )}
                    <div className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2 text-gray-600">
                        <CreditCard className="w-4 h-4" />
                        <span>Trial Days</span>
                      </div>
                      <span className="font-semibold text-gray-900">{plan.trial_days}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2 text-gray-600">
                        <Check className="w-4 h-4" />
                        <span>LOI Verifications/mo</span>
                      </div>
                      <span className="font-semibold text-gray-900">{plan.loi_verifications_per_month || 'Unlimited'}</span>
                    </div>
                    {/* Features */}
                    {plan.features && plan.features.length > 0 && (
                      <div className="pt-4 border-t border-gray-100">
                        <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Features</p>
                        <ul className="space-y-2">
                          {plan.features.map((feature, idx) => (
                            <li key={idx} className="flex items-center gap-2 text-sm text-gray-600">
                              <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                              <span>{feature}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="p-4 bg-gray-50 border-t border-gray-100 flex items-center justify-end gap-2">
                    <button
                      onClick={() => openEditModal(plan)}
                      className="p-2 text-gray-600 hover:bg-gray-200 rounded-lg transition"
                      title="Edit"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => {
                        if (plan.subscriber_count > 0) {
                          toast.error(`Cannot delete plan with ${plan.subscriber_count} active subscriber(s)`);
                          return;
                        }
                        handleDelete(plan.id);
                      }}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition"
                      title={plan.subscriber_count > 0 ? `${plan.subscriber_count} subscribers — cannot delete` : 'Deactivate plan'}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">
                {editingPlan ? 'Edit Plan' : 'Create New Plan'}
              </h3>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Plan Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., Professional"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <input
                  type="text"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Brief description of the plan"
                />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Monthly Price *</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.price}
                    onChange={(e) => {
                      const updates = { price: e.target.value };
                      // Only auto-fill when creating NEW plans (not editing)
                      if (!editingPlan) {
                        const monthly = parseFloat(e.target.value) || 0;
                        if (formData.yearly_price === '' || formData.yearly_price === null || formData.yearly_price === undefined) {
                          updates.yearly_price = monthly > 0 ? Math.round(monthly * 12 * 0.8 * 100) / 100 : '';
                        }
                        if (formData.lifetime_price === '' || formData.lifetime_price === null || formData.lifetime_price === undefined) {
                          updates.lifetime_price = monthly > 0 ? Math.round(monthly * 36 * 100) / 100 : '';
                        }
                      }
                      setFormData(prev => ({ ...prev, ...updates }));
                    }}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="29.00"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Yearly Price</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.yearly_price}
                    onChange={(e) => setFormData({ ...formData, yearly_price: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="290.00"
                  />
                  {formData.price > 0 && (
                    <p className="text-xs text-gray-400 mt-1">
                      Suggested: ${Math.round(parseFloat(formData.price) * 12 * 0.8 * 100) / 100} (20% off)
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Lifetime Price</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.lifetime_price}
                    onChange={(e) => setFormData({ ...formData, lifetime_price: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="999.00"
                  />
                  {formData.price > 0 && (
                    <p className="text-xs text-gray-400 mt-1">
                      Suggested: ${Math.round(parseFloat(formData.price) * 36 * 100) / 100} (3 yrs)
                    </p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">User Limit</label>
                  <input
                    type="number"
                    min="1"
                    value={formData.user_limit}
                    onChange={(e) => setFormData({ ...formData, user_limit: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Client Limit</label>
                  <input
                    type="number"
                    min="1"
                    value={formData.client_limit}
                    onChange={(e) => setFormData({ ...formData, client_limit: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Trial Days</label>
                  <input
                    type="number"
                    min="0"
                    value={formData.trial_days}
                    onChange={(e) => setFormData({ ...formData, trial_days: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">LOI Verifications/mo</label>
                  <input
                    type="number"
                    min="0"
                    value={formData.loi_verifications_per_month}
                    onChange={(e) => setFormData({ ...formData, loi_verifications_per_month: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="Unlimited"
                  />
                </div>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="show_client_limit"
                  checked={formData.show_client_limit}
                  onChange={(e) => setFormData({ ...formData, show_client_limit: e.target.checked })}
                  className="w-4 h-4 rounded border-gray-300"
                />
                <label htmlFor="show_client_limit" className="text-sm text-gray-700">Show client limit on billing page (company admin view)</label>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Features (one per line)</label>
                <textarea
                  value={formData.features}
                  onChange={(e) => setFormData({ ...formData, features: e.target.value })}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Advanced Quote Process&#10;Priority Support&#10;Custom Branding"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Badge Text</label>
                  <input
                    type="text"
                    value={formData.badge_text}
                    onChange={(e) => setFormData({ ...formData, badge_text: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="Most Popular"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Button Text</label>
                  <input
                    type="text"
                    value={formData.cta_text}
                    onChange={(e) => setFormData({ ...formData, cta_text: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    placeholder="Start 14-Day Free Trial"
                  />
                </div>
              </div>

              <div className="flex items-center gap-6">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="is_active"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    className="w-4 h-4 rounded border-gray-300"
                  />
                  <label htmlFor="is_active" className="text-sm text-gray-700">Active</label>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="is_popular"
                    checked={formData.is_popular}
                    onChange={(e) => setFormData({ ...formData, is_popular: e.target.checked })}
                    className="w-4 h-4 rounded border-amber-400 text-amber-500"
                  />
                  <label htmlFor="is_popular" className="text-sm text-gray-700">Most Popular (shows badge on pricing page)</label>
                </div>
              </div>

              <div className="flex items-center gap-3 justify-end pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => { setShowModal(false); resetForm(); }}
                  className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
                >
                  {editingPlan ? 'Save Changes' : 'Create Plan'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Sidebar>
  );
};

export default MainAdminMembershipPlans;
